/**
 * 健康调查问卷题目配置
 * @typedef {'number'|'single'|'multiple'} SurveyQuestionType
 * @typedef {{ value: string, label: string }} SurveyOption
 * @typedef {{
 *   id: string,
 *   type: SurveyQuestionType,
 *   label: string,
 *   unit?: string,
 *   placeholder?: string,
 *   options?: SurveyOption[],
 *   noneValue?: string
 * }} SurveyQuestion
 * @typedef {{ id: string, title: string, questions: SurveyQuestion[] }} SurveySection
 */

/** @type {SurveySection[]} */
export const HEALTH_SURVEY_SECTIONS = [
  {
    id: 'basic',
    title: '一、基本信息',
    questions: [
      {
        id: 'q1',
        type: 'number',
        label: '1. 您的年龄',
        unit: '岁',
        placeholder: '请输入年龄'
      },
      {
        id: 'q2',
        type: 'single',
        label: '2. 您的性别',
        options: [
          { value: 'male', label: '男' },
          { value: 'female', label: '女' }
        ]
      },
      {
        id: 'q3',
        type: 'number',
        label: '3. 您的身高',
        unit: 'cm',
        placeholder: '请输入身高'
      },
      {
        id: 'q4',
        type: 'number',
        label: '4. 您的体重',
        unit: 'kg',
        placeholder: '请输入体重'
      }
    ]
  },
  {
    id: 'bloodPressure',
    title: '二、血压与心脏健康',
    questions: [
      {
        id: 'q5',
        type: 'single',
        label: '5. 您是否曾被医生告知患有高血压？',
        options: [
          { value: 'never', label: '从未' },
          { value: 'grade12_controlled', label: '曾确诊过高血压1级或2级，血压一直控制在179/109 mmHg以内' },
          { value: 'grade3_controlled', label: '曾确诊过高血压3级，但近半年血压一直控制在179/109 mmHg以内' },
          { value: 'grade3_high', label: '曾确诊过高血压3级，血压一直较高（经常超过180/110 mmHg）' },
          { value: 'unknown', label: '不清楚' }
        ]
      },
      {
        id: 'q6',
        type: 'single',
        label: '6. 您最近一次测量的血压最高值大约是多少？（可参考体检或家中测量）',
        options: [
          { value: 'below_139_89', label: '低于139/89 mmHg' },
          { value: '140_159', label: '140–159 / 90–99 mmHg' },
          { value: '160_179', label: '160–179 / 100–109 mmHg' },
          { value: '180_110_plus', label: '180/110 mmHg 或更高' },
          { value: 'never_unknown', label: '从未测过 / 不清楚' }
        ]
      },
      {
        id: 'q7',
        type: 'single',
        label: '7. 医生是否曾告知您，高血压已经影响到您的心脏、大脑、肾脏或眼睛？（例如：心室肥厚、脑梗、肾功能异常、眼底病变等）',
        options: [
          { value: 'yes', label: '是' },
          { value: 'no', label: '否' }
        ]
      },
      {
        id: 'q8',
        type: 'multiple',
        label: '8. 您是否曾被诊断有以下任何一种心脏问题？（可多选）',
        noneValue: 'none',
        options: [
          { value: 'coronary', label: '冠心病' },
          { value: 'angina', label: '心绞痛' },
          { value: 'mi', label: '心肌梗死（心梗）' },
          { value: 'ventricular_aneurysm', label: '室壁瘤' },
          { value: 'cardiomyopathy', label: '心肌病' },
          { value: 'rheumatic', label: '风湿性心脏病' },
          { value: 'valve', label: '心脏瓣膜病' },
          { value: 'pulmonary_heart', label: '肺源性心脏病' },
          { value: 'heart_failure', label: '心功能不全二级（含）以上（如心力衰竭）' },
          { value: 'af', label: '心房颤动（房颤）/扑动' },
          { value: 'pericardial', label: '心包疾病' },
          { value: 'endocarditis', label: '心内膜炎' },
          { value: 'av_block', label: '二度二型或三度房室传导阻滞' },
          { value: 'aortic', label: '主动脉疾病（如主动脉瘤、狭窄）' },
          { value: 'none', label: '以上都没有' }
        ]
      }
    ]
  },
  {
    id: 'diabetes',
    title: '三、血糖与糖尿病',
    questions: [
      {
        id: 'q9',
        type: 'single',
        label: '9. 您是否曾被医生告知患有糖尿病？',
        options: [
          { value: 'never', label: '从未' },
          { value: 'type1', label: '有，1型糖尿病' },
          { value: 'type2', label: '有，2型糖尿病' },
          { value: 'gestational', label: '有，妊娠期糖尿病（已恢复）' },
          { value: 'unknown', label: '不清楚' }
        ]
      },
      {
        id: 'q10',
        type: 'single',
        label: '10. 如果是2型糖尿病，最近3个月内您的空腹血糖最高值大约是？',
        options: [
          { value: 'below_7', label: '低于7.0 mmol/L' },
          { value: '7_8_9', label: '7.0–8.9 mmol/L' },
          { value: '9_plus', label: '9.0 mmol/L 或更高' },
          { value: 'unknown', label: '不清楚' }
        ]
      },
      {
        id: 'q11',
        type: 'single',
        label: '11. 最近3个月内您的糖化血红蛋白（HbA1c）值大约是？',
        options: [
          { value: 'below_6_5', label: '低于6.5%' },
          { value: '6_5_6_9', label: '6.5%–6.9%' },
          { value: '7_plus', label: '7.0% 或更高' },
          { value: 'unknown', label: '不清楚' }
        ]
      },
      {
        id: 'q12',
        type: 'single',
        label: '12. 医生是否曾告知您，糖尿病已经影响到您的心脏、大脑、眼睛、肾脏或神经？（例如：糖尿病视网膜病变、糖尿病肾病、糖尿病足等）',
        options: [
          { value: 'yes', label: '是' },
          { value: 'no', label: '否' }
        ]
      }
    ]
  },
  {
    id: 'cerebrovascular',
    title: '四、脑血管与神经系统',
    questions: [
      {
        id: 'q13',
        type: 'multiple',
        label: '13. 您是否曾被诊断有以下任何一种脑部或血管问题？（可多选）',
        noneValue: 'none',
        options: [
          { value: 'cerebral_infarction', label: '脑梗死（脑血栓、脑栓塞）' },
          { value: 'cerebral_hemorrhage', label: '脑出血' },
          { value: 'tia', label: '短暂性脑缺血发作（小中风）' },
          { value: 'vascular_malformation', label: '脑血管畸形 / 动脉瘤' },
          { value: 'arterial_stenosis', label: '动脉狭窄（＞50%）' },
          { value: 'thrombosis', label: '血栓' },
          { value: 'vasculitis', label: '血管炎' },
          { value: 'subarachnoid', label: '蛛网膜下腔出血' },
          { value: 'epilepsy', label: '癫痫' },
          { value: 'brain_injury', label: '脑外伤后遗症（如认知、运动、感觉、精神障碍）' },
          { value: 'none', label: '以上都没有' }
        ]
      }
    ]
  },
  {
    id: 'otherDiseases',
    title: '五、呼吸与肾脏及其他重要疾病',
    questions: [
      {
        id: 'q14',
        type: 'multiple',
        label: '14. 您是否曾被诊断有以下任何一种疾病？（可多选）',
        noneValue: 'none',
        options: [
          { value: 'copd', label: '慢性阻塞性肺病（慢阻肺）' },
          { value: 'pulmonary_hypertension', label: '肺动脉高压' },
          { value: 'respiratory_failure', label: '慢性呼吸功能衰竭' },
          { value: 'pulmonary_embolism', label: '肺栓塞' },
          { value: 'chronic_nephritis', label: '慢性肾炎 / 肾病综合征' },
          { value: 'renal_insufficiency', label: '肾功能不全' },
          { value: 'hyperaldosteronism', label: '醛固酮增多' },
          { value: 'cushing', label: '库欣综合征' },
          { value: 'pheochromocytoma', label: '嗜铬细胞瘤' },
          { value: 'sle', label: '系统性红斑狼疮' },
          { value: 'aplastic_anemia', label: '再生障碍性贫血' },
          { value: 'malignancy', label: '恶性肿瘤（不包括已治愈的甲状腺乳头状癌及皮肤基底细胞癌）' },
          { value: 'intracranial_cardiac_tumor', label: '颅内或心脏肿瘤' },
          { value: 'retinal_disease', label: '眼底病变/视网膜病变' },
          { value: 'blindness', label: '失明' },
          { value: 'paralysis', label: '瘫痪' },
          { value: 'transplant', label: '接受器官移植或造血干细胞移植术' },
          { value: 'mental_illness', label: '精神疾病（精神分裂症、双相情感障碍、抑郁症、焦虑症）' },
          { value: 'alcoholism', label: '酗酒或酒精中毒' },
          { value: 'none', label: '以上都没有' }
        ]
      }
    ]
  },
  {
    id: 'recent',
    title: '六、近期症状与检查',
    questions: [
      {
        id: 'q15',
        type: 'multiple',
        label: '15. 过去1年内，您是否经常出现以下症状？（可多选）',
        noneValue: 'none',
        options: [
          { value: 'headache', label: '反复头痛' },
          { value: 'syncope', label: '晕厥' },
          { value: 'chest_pain', label: '胸痛' },
          { value: 'palpitation', label: '心慌（心悸）' },
          { value: 'cyanosis', label: '皮肤或眼睛发紫（紫绀）' },
          { value: 'edema', label: '浮肿' },
          { value: 'proteinuria', label: '蛋白尿' },
          { value: 'gout', label: '痛风发作（大于2次）' },
          { value: 'high_uric_acid', label: '血尿酸＞720μmol/L' },
          { value: 'none', label: '以上都没有' }
        ]
      },
      {
        id: 'q16',
        type: 'multiple',
        label: '16. 过去2年内，您是否做过以下检查且结果提示异常？（可多选）',
        noneValue: 'none',
        options: [
          { value: 'ecg', label: '心电图' },
          { value: 'echo', label: '超声心动图（心脏彩超）' },
          { value: 'carotid', label: '颈动脉彩超' },
          { value: 'cerebrovascular', label: '脑血管检查（如经颅多普勒、MRA）' },
          { value: 'coronary', label: '冠脉CTA或冠脉造影' },
          { value: 'cardiac_markers', label: '心肌损伤标志物（乳酸脱氢酶及其同工酶、肌酸激酶、肌酸激酶同工酶、肌钙蛋白）' },
          { value: 'none', label: '以上都没有 / 没做过这些检查' }
        ]
      },
      {
        id: 'q17',
        type: 'single',
        label: '17. 过去2年内，您是否曾因健康问题住院治疗？或者医生建议住院但您未遵医嘱？',
        options: [
          { value: 'yes', label: '是' },
          { value: 'no', label: '否' }
        ]
      }
    ]
  },
  {
    id: 'lifestyle',
    title: '七、生活习惯与其他',
    questions: [
      {
        id: 'q18',
        type: 'single',
        label: '18. 您的体重情况（可参考BMI，BMI=体重（kg）÷身高（m）^2）：',
        options: [
          { value: 'underweight', label: '偏瘦（BMI < 18.5）' },
          { value: 'normal', label: '正常（18.5≦ BMI ≦23.9）' },
          { value: 'overweight', label: '超重（24≦ BMI ≦27.9）' },
          { value: 'obese', label: '肥胖（28≦ BMI ≦32）' },
          { value: 'severe_obese', label: '严重肥胖（BMI > 32）' }
        ]
      }
    ]
  }
]

/** 简易问卷编码（对应 SurveyTypeEnum.CVH_SIMPLE） */
export const HEALTH_SURVEY_CODE = 'CVH_SIMPLE'

/** 简易问卷标题 */
export const HEALTH_SURVEY_TITLE = '健康调查问卷'

/** 资料附件题目 ID（提交 content 用） */
export const HEALTH_SURVEY_ATTACHMENT_ID = 'attachment'

/** 资料附件题目标题 */
export const HEALTH_SURVEY_ATTACHMENT_LABEL = '资料上传'

/** 资料附件上传说明 */
export const HEALTH_SURVEY_ATTACHMENT_DESC =
  '上传一年内心血管疾病相关的资料，如门诊诊断/处方/病历/检查报告，或住院诊断/处方/病历/出院小结/检查报告，提供一项或多项均可'

/** 资料附件最多上传数量 */
export const HEALTH_SURVEY_ATTACHMENT_MAX_COUNT = 9

/** 小程序上传场景标识（用于 mpType 回传区分） */
export const HEALTH_SURVEY_ATTACHMENT_SCENE = 'HEALTH_SURVEY_ATTACHMENT'

/** 推荐人题目 ID（提交 content 用） */
export const HEALTH_SURVEY_REFERRER_ID = 'referrer'

/** 推荐人题目标题 */
export const HEALTH_SURVEY_REFERRER_LABEL = '推荐人'



// WEBPACK FOOTER //
// ./src/views/mine/healthSurvey/healthSurveyQuestions.js